import { useCallback, useRef } from "react";

import { ReportCell } from "@/types/evaluate";
import { useGetUpdatedReportCells, useRefreshCells, useGetSortedRowIds } from "@/queries";
import { useAuth } from "@/context/auth-context";
import { REPORT_CELL_REFRESH, REPORT_VISIBLE_CELL_REFRESH } from "@/constants/ReportConstants";
import { useReportContext } from "@/components/Evaluate/report-context";

export const useReportCells = ({
  searchTerm,
  filters,
}: {
  searchTerm?: string;
  filters?: Record<string, string[]>;
}) => {
  const reportContext = useReportContext();
  const userToken = useAuth()?.userToken || "";
  const idRefreshCellsRef = useRef<{
    mutate: (params: { reportId: number; cellIds: number[] }) => void;
  } | null>(null);

  useGetSortedRowIds(
    userToken,
    reportContext.reportId,
    searchTerm ?? null,
    reportContext.sortConfig,
    filters ?? {},
    reportContext.cellStatus,
    data => {
      reportContext.setSortedRowIds(data);
    },
  );

  const updateCells = useCallback(
    (updatedCells: ReportCell[]) => {
      const toRefresh: number[] = [];
      let latest: Date | null = null;
      reportContext.setReportCells((prev: ReportCell[] | null) => {
        const newCells = prev ? [...prev] : [];
        updatedCells.forEach(newResult => {
          let added = false;
          for (let i = 0; i < newCells.length; i += 1) {
            if (newCells[i].id === newResult.id) {
              newCells[i] = newResult;
              added = true;
              break;
            }
          }
          const newDate = new Date(newResult.updated_at);
          if (latest === null || latest < newDate) {
            latest = newDate;
          }
          if (!added) {
            newCells.push(newResult);
          }
        });
        return newCells;
      });
      reportContext.setLastUpdated((prev: Date | undefined) => {
        if (latest !== null && (prev === undefined || latest > prev)) {
          return latest;
        }
        return prev;
      });
      if (toRefresh.length > 0 && idRefreshCellsRef.current) {
        idRefreshCellsRef.current.mutate({
          reportId: reportContext.reportId,
          cellIds: toRefresh,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportContext.setReportCells, reportContext.reportId],
  );

  const idRefreshCells = useRefreshCells(userToken, updateCells);
  idRefreshCellsRef.current = idRefreshCells;

  useGetUpdatedReportCells(
    userToken,
    REPORT_CELL_REFRESH,
    reportContext.reportId,
    reportContext.visibleRows,
    reportContext.lastUpdated,
    data => {
      if (data) {
        updateCells(data);
      }
    },
  );

  useGetUpdatedReportCells(
    userToken,
    REPORT_VISIBLE_CELL_REFRESH,
    reportContext.reportId,
    reportContext.visibleRows,
    undefined,
    data => {
      if (data) {
        reportContext.setReportCells(() => data);
      }
    },
  );
};
